import { CartoLayer } from '@deck.gl/carto';
import { CompositeLayer } from '@deck.gl/core';
import { useDispatch, useSelector } from 'react-redux';
import { useCartoLayerProps } from '@carto/react-api';
import stateBroadbandSource from 'data/sources/stateBroadbandSource';
import { broadbandColor } from 'utils/broadbandScores';
import { useState, useCallback, useEffect } from 'react';
import { LAYER_TYPE, isLayerVisible } from 'utils/layerVisibility';
import { setViewState } from '@carto/react-redux';
import { useSearchParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import Popover from '../common/Popover';

export const STATE_BROADBAND_LAYER_ID = 'stateBroadbandLayer';
export const STATE_BROADBAND_LAYER_HOVER_ID = 'stateBroadbandLayerHover';
export const STATE_BOUNDARIES_LAYER_ID = 'stateBoundariesLayer';
export const ACTIVE_STATE_BROADBAND_LAYER_ID = 'activeStateBroadbandLayer';

const ACTIVE_COMPARE_STATE_A_COLOR = [99, 196, 89];
const ACTIVE_COMPARE_STATE_B_COLOR = [210, 104, 208];

export default function StateBroadbandLayer() {
  const [layer, setLayer] = useState(null);
  const [previousActiveState, setPreviousActiveState] = useState(null);
  const [previousActiveCompareState, setPreviousActiveCompareState] = useState(null);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);
  const [previousHoveredStateName, setPreviousHoveredStateName] = useState(null);
  const [previousYear, setPreviousYear] = useState(null);
  const [previousActive, setPreviousActive] = useState(null);
  const [previousActivePanel, setPreviousActivePanel] = useState(null);
  const [previousBroadbandMin, setPreviousBroadbandMin] = useState(null);
  const [previousBroadbandMax, setPreviousBroadbandMax] = useState(null);
  const { stateBroadbandLayer } = useSelector((state) => state.carto.layers);
  const source = stateBroadbandSource(stateBroadbandLayer?.year || 2019);
  const cartoLayerProps = useCartoLayerProps({ source });
  const viewState = useSelector((state) => state.carto.viewState);
  const [hoveredStateName, setHoveredStateName] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    let activeState = searchParams.get('active-state') || null;
    let activeCounty = searchParams.get('active-county') || null;
    let activeTract = searchParams.get('active-tract') || null;

    setActive(activeState || activeCounty || activeTract);
  }, [active, searchParams, setActive]);

  const _zoomStateIntoView = useCallback(
    ({ lng, lat }) => {
      dispatch(
        setViewState({
          // zoom: 6,
          latitude: lat,
          longitude: lng,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (!stateBroadbandLayer || !source) {
      return;
    }

    if (isLayerVisible(LAYER_TYPE.STATE, viewState.zoom) === false) {
      setLayer(null);
      return;
    }

    if (
      layer &&
      previousActive === active &&
      previousActiveState === searchParams.get('active-state') &&
      previousActiveCompareState === searchParams.get('active-compare-state') &&
      previousSocioeconomicFactor === searchParams.get('socioeconomic-factor') &&
      previousHoveredStateName === hoveredStateName &&
      previousYear === searchParams.get('year') &&
      previousActivePanel === searchParams.get('active-panel') &&
      previousBroadbandMin === searchParams.get('broadband-min') &&
      previousBroadbandMax === searchParams.get('broadband-max')
    ) {
      return;
    }

    let mainStateBroadbandLayer = new CartoLayer({
      ...cartoLayerProps,
      id: STATE_BROADBAND_LAYER_ID,
      getFillColor: (object) => {
        let broadbandScore = object.properties.broadband;
        let stateName = object.properties.name;
        let isActiveCompareState = searchParams.get('active-compare-state') === stateName;

        if (isActiveCompareState === true) {
          return ACTIVE_COMPARE_STATE_B_COLOR;
        }

        if (
          searchParams.get('active-panel') === 'compare-states' &&
          searchParams.get('active-state') === stateName
        ) {
          return ACTIVE_COMPARE_STATE_A_COLOR;
        }

        if (
          searchParams.get('active-state') === stateName ||
          hoveredStateName === stateName
        ) {
          return broadbandColor(broadbandScore);
        }

        return broadbandColor(
          broadbandScore,
          active !== null ||
            object.properties.broadband < searchParams.get('broadband-min') ||
            object.properties.broadband > searchParams.get('broadband-max')
        );
      },
      getLineColor: [255, 255, 255],
      pointRadiusMinPixels: 2,
      // opacity: isLayerVisible(LAYER_TYPE.STATE, viewState.zoom) ? 1 : 0,
      lineWidthMinPixels: 1,
      pickable: false,
      updateTriggers: {
        opacity: [viewState],
        getFillColor: [
          searchParams.get('active-compare-state'),
          searchParams.get('active-state'),
          searchParams.get('active-panel'),
          searchParams.get('year'),
          searchParams.get('broadband-min'),
          searchParams.get('broadband-max'),
          hoveredStateName,
          active,
        ],
      },
    });

    // let _stateBoundariesLayer = new CartoLayer({
    //   ...cartoLayerProps,
    //   id: STATE_BOUNDARIES_LAYER_ID,
    //   getFillColor: [0, 0, 0, 0],
    //   pointRadiusMinPixels: 2,
    //   getLineColor: [255, 255, 255],
    //   lineWidthMinPixels:
    //     viewState.zoom < MAX_ZOOM_LEVEL[LAYER_TYPE.COUNTY] ? 1 : viewState.zoom * 0.7,
    // });

    let stateBroadbandHoverLayer = new CartoLayer({
      ...cartoLayerProps,
      id: STATE_BROADBAND_LAYER_HOVER_ID,
      getFillColor: [0, 0, 0, 0],
      pointRadiusMinPixels: 2,
      getLineColor: (object) => {
        return hoveredStateName === object.properties.name &&
          searchParams.get('active-compare-state') !== hoveredStateName &&
          searchParams.get('active-state') !== hoveredStateName
          ? [6, 49, 93]
          : [255, 255, 255, 0];
      },
      // opacity: isLayerVisible(LAYER_TYPE.STATE, viewState.zoom) ? 1 : 0,
      lineWidthMinPixels: 1.5,
      // pickable: isLayerVisible(LAYER_TYPE.STATE, viewState.zoom),
      pickable: true,
      updateTriggers: {
        getLineColor: [
          hoveredStateName,
          searchParams.get('active-compare-state'),
          searchParams.get('active-state'),
        ],
      },
    });

    let activeStateBroadbandLayer = new CartoLayer({
      ...cartoLayerProps,
      id: ACTIVE_STATE_BROADBAND_LAYER_ID,
      getFillColor: [0, 0, 0, 0],
      pointRadiusMinPixels: 2,
      getLineColor: (object) => {
        let stateName = object.properties.name;

        if (
          searchParams.get('active-state') === stateName ||
          searchParams.get('active-compare-state') === stateName ||
          hoveredStateName === stateName
        ) {
          return [6, 49, 93];
        }

        return [255, 255, 255, 0];
      },
      // opacity: isLayerVisible(LAYER_TYPE.STATE, viewState.zoom) ? 1 : 0,
      lineWidthMinPixels: 1.5,
      // pickable: isLayerVisible(LAYER_TYPE.STATE, viewState.zoom),
      pickable: true,
      updateTriggers: {
        pickable: [viewState],
        opacity: [viewState],
        getLineColor: [
          searchParams.get('active-state'),
          searchParams.get('active-compare-state'),
          hoveredStateName,
        ],
      },
    });

    class StateBroadbandCompositeLayer extends CompositeLayer {
      renderLayers() {
        return [
          mainStateBroadbandLayer,
          stateBroadbandHoverLayer,
          activeStateBroadbandLayer,
        ];
        // return [stateBroadbandLayer, stateBoundariesLayer, stateBroadbandHoverLayer];
      }
    }

    let stateBroadbandCompositeLayer = new StateBroadbandCompositeLayer({
      onHover: (info) => {
        setHoveredStateName(info.object?.properties?.name || null);

        if (info?.object) {
          info.object = {
            html: ReactDOMServer.renderToStaticMarkup(
              <Popover>
                <div className='whitespace-nowrap flex flex-col gap-1'>
                  <div className='flex gap-12 items-center justify-between'>
                    {info.object?.properties?.name}
                    <div className='text-blue-300 text-sm'>
                      ({info.object?.properties?.year})
                    </div>
                  </div>
                  <div className='text-sm'>
                    Broadband:{' '}
                    <span className='text-blue-300'>
                      {Math.round(info.object?.properties?.broadband * 100) / 100}
                    </span>
                  </div>
                </div>
              </Popover>
            ),
            style: {
              background: 'transparent',
            },
          };
        }
      },
      onClick: (info) => {
        setSearchParams((params) => {
          params.set('active-state', info.object.properties.name);
          params.delete('active-tract');
          params.delete('active-county');
          params.delete('active-panel');
          params.delete('active-compare-county');
          params.delete('active-compare-state');
          params.delete('active-compare-tract');
          params.delete('active-compare-similar-tract');

          return params;
        });

        // zoomStateIntoView({
        //   lng: info.object.properties.lng,
        //   lat: info.object.properties.lat,
        // });
      },
      updateTriggers: {
        getLineColor: [
          searchParams.get('active-state'),
          searchParams.get('active-compare-state'),
          searchParams.get('year'),
          active,
          hoveredStateName,
        ],
        lineWidthMinPixels: [hoveredStateName],
        opacity: [viewState, active],
        getFillColor: [
          searchParams.get('active-compare-tract'),
          searchParams.get('active-tract'),
          searchParams.get('year'),
          searchParams.get('active-panel'),
          searchParams.get('broadband-min'),
          searchParams.get('broadband-max'),
          hoveredStateName,
          active,
        ],
      },
    });

    setLayer(stateBroadbandCompositeLayer);
    setPreviousActive(active);
    setPreviousActiveState(searchParams.get('active-state'));
    setPreviousActiveCompareState(searchParams.get('active-compare-state'));
    setPreviousSocioeconomicFactor(searchParams.get('socioeconomic-factor'));
    setPreviousYear(searchParams.get('year'));
    setPreviousActivePanel(searchParams.get('active-panel'));
    setPreviousHoveredStateName(hoveredStateName);
    setPreviousBroadbandMin(searchParams.get('broadband-min'));
    setPreviousBroadbandMax(searchParams.get('broadband-max'));
  }, [
    active,
    cartoLayerProps,
    hoveredStateName,
    layer,
    previousActive,
    previousActiveCompareState,
    previousActivePanel,
    previousActiveState,
    previousBroadbandMax,
    previousBroadbandMin,
    previousHoveredStateName,
    previousSocioeconomicFactor,
    previousYear,
    searchParams,
    setSearchParams,
    source,
    stateBroadbandLayer,
    viewState,
  ]);

  return layer;
}
